import React, { useState } from 'react';
import { Instrument } from '../types/Instrument';
import { CreateEvent } from '../types/Event';

interface InstrumentsPageProps {
  eventType: string;
  eventDetails: CreateEvent;
  instruments: Instrument[];
  handleInstrumentChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const InstrumentsPage: React.FC<InstrumentsPageProps> = ({
  eventType,
  eventDetails,
  instruments,
  handleInstrumentChange
}) => {
  // Only show the instruments page if the event type is "MUSIC"
  if (eventType !== 'MUSIC') return null;

  // Initialize state for the filters
  const [filters, setFilters] = useState({
    isSpecial: false,
    isConcertBand: false,
    isMarchingBand: false,
    isJazzBand: false,
    isOrchestra: false,
    isStrings: false,
    isChoir: false,
  });

  // Function to handle filter checkbox changes
  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: checked,
    }));
  };

  // Function to filter instruments based on selected filters
  const filterInstruments = (instrument: Instrument) => {
    // If "Special" is unchecked, only show instruments that are not special
    if (!filters.isSpecial && instrument.is_special) {
      return false;
    }

    // If no band types are selected, show all instruments (based on the "Special" filter)
    const selectedCategories = [
      filters.isConcertBand,
      filters.isMarchingBand,
      filters.isJazzBand,
      filters.isOrchestra,
      filters.isStrings,
      filters.isChoir,
    ];

    // If no categories are selected, show all instruments based on "Special" filter
    if (selectedCategories.every((category) => !category)) {
      return true; // Show all instruments if no band types are selected
    }

    // OR logic: If any of the selected categories match, show the instrument
    const matchesCategory =
      (filters.isConcertBand && instrument.is_concert_band) ||
      (filters.isMarchingBand && instrument.is_marching_band) ||
      (filters.isJazzBand && instrument.is_jazz_band) ||
      (filters.isOrchestra && instrument.is_orchestra) ||
      (filters.isStrings && instrument.is_strings) ||
      (filters.isChoir && instrument.is_choir);

    return matchesCategory;
  };

  // Group instruments by their type
  const groupedInstruments = instruments.reduce((groups, instrument) => {
    const { instrument_type_name } = instrument;
    if (!groups[instrument_type_name]) {
      groups[instrument_type_name] = [];
    }
    groups[instrument_type_name].push(instrument);
    return groups;
  }, {} as Record<string, Instrument[]>);

  // Filtered instruments based on the filters
  const filteredInstruments = instruments.filter(filterInstruments);

  // Filter out any categories that don't have any instruments after filtering
  const nonEmptyGroups = Object.entries(groupedInstruments).filter(([typeName, instrumentGroup]) => {
    // Only include the group if there are filtered instruments in that category
    return instrumentGroup.some((instrument) => filteredInstruments.includes(instrument));
  });

  return (
    <div>
      <h2>Instruments</h2>
      <p>Select the instruments for the event:</p>

      {/* Filter checkboxes */}
      <div>
        <h3>Filter by type</h3>
        <div>
          <label>
            <input
              type="checkbox"
              name="isSpecial"
              checked={filters.isSpecial}
              onChange={handleFilterChange}
            />
            Show Special
          </label>
          <label>
            <input
              type="checkbox"
              name="isConcertBand"
              checked={filters.isConcertBand}
              onChange={handleFilterChange}
            />
            Concert Band
          </label>
          <label>
            <input
              type="checkbox"
              name="isMarchingBand"
              checked={filters.isMarchingBand}
              onChange={handleFilterChange}
            />
            Marching Band
          </label>
          <label>
            <input
              type="checkbox"
              name="isJazzBand"
              checked={filters.isJazzBand}
              onChange={handleFilterChange}
            />
            Jazz Band
          </label>
          <label>
            <input
              type="checkbox"
              name="isOrchestra"
              checked={filters.isOrchestra}
              onChange={handleFilterChange}
            />
            Orchestra
          </label>
          <label>
            <input
              type="checkbox"
              name="isStrings"
              checked={filters.isStrings}
              onChange={handleFilterChange}
            />
            Strings
          </label>
          <label>
            <input
              type="checkbox"
              name="isChoir"
              checked={filters.isChoir}
              onChange={handleFilterChange}
            />
            Choir
          </label>
        </div>
      </div>

      {/* Render filtered instruments */}
      {nonEmptyGroups.length === 0 ? (
        <p>No instruments match the selected filters.</p>
      ) : (
        nonEmptyGroups.map(([typeName, instrumentGroup]) => (
          <div key={typeName} style={{ marginBottom: '20px' }}>
            <h3>{typeName}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
              {instrumentGroup
                .filter((instrument) => filteredInstruments.includes(instrument)) // Only display filtered instruments
                .map((instrument) => (
                  <div key={instrument.instrument_id} style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      id={`instrument-${instrument.instrument_id}`}
                      name="instrument_ids"
                      value={instrument.instrument_id}
                      checked={eventDetails.instrument_ids?.includes(instrument.instrument_id)}
                      onChange={handleInstrumentChange}
                    />
                    <label htmlFor={`instrument-${instrument.instrument_id}`} style={{ marginLeft: '8px' }}>
                      {instrument.instrument_name}
                    </label>
                  </div>
                ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};
